import { v4 as uuidv4 } from 'uuid';
import { getAllSegments, getClipFPS } from '@goldcast/player-config/src/utils/clipUtils';
import { Clip } from '@goldcast/player-config/src/types/clip-types';
import { TrackType } from '@goldcast/player-config/src/types/Track/Type';
import { ElementsTrack } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Track/ElementsTrack';
import { Element } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Element/Element';
import { injectSegmentMetaDataInCompositeSegment } from '@goldcast/player-config/src/generators/playerConfigHelpers';
import { approxLTE } from '@goldcast/player-config/src/utils/compareUtils';
import { getPlayerTimeFromSegmentTime } from '../timeUtils';
import { updateCaptions } from './captionUtils';
import { splitSegment } from './compositeUtils';
import { insertElementIntoNewNonOverlappingTrack } from '@/Pages/Clip/ClipPlayer/ClipAssets/utils';
import { getPlayerConfigHelper } from '@/App/remotion/MainPlayer/utils/playerConfig/config';

export const shiftElementTracksRight = (tracks: ElementsTrack[], offsetFrames: number): ElementsTrack[] => {
  return tracks.map(track => ({
    ...track,
    elements: track.elements.map(el => ({
      ...el,
      fromFrame: el.fromFrame + offsetFrames,
      toFrame: el.toFrame + offsetFrames
    }))
  }));
};

export const cloneOverlaysForHook = (
  clipData: Clip,
  selectionStartFrame: number,
  hookDurationFrames: number,
  baseTracks: ElementsTrack[] = []
): ElementsTrack[] => {
  const selectionEndFrame = selectionStartFrame + hookDurationFrames;
  const tracks = clipData.asset_metadata.player_config?.playerConfig?.tracks ?? [];
  const elementTracks = tracks.filter(track => track.type === TrackType.ELEMENTS);

  let clonedTracks: ElementsTrack[] = baseTracks;
  for (const track of elementTracks) {
    for (const element of track.elements) {
      if (element.toFrame <= selectionStartFrame || element.fromFrame >= selectionEndFrame) {
        continue;
      }

      const clonedFromFrame = Math.max(0, element.fromFrame - selectionStartFrame);
      const clonedToFrame = Math.min(hookDurationFrames, element.toFrame - selectionStartFrame);
      if (clonedFromFrame >= clonedToFrame) {
        continue;
      }

      const clonedElement: Element = {
        ...structuredClone(element),
        id: uuidv4(),
        fromFrame: clonedFromFrame,
        toFrame: clonedToFrame
      };
      clonedTracks = insertElementIntoNewNonOverlappingTrack(clonedTracks, clonedElement);
    }
  }
  return clonedTracks;
};

export const buildHookElementTracks = ({
  clip,
  playerStartTime,
  playerEndTime
}: {
  clip: Clip;
  playerStartTime: number;
  playerEndTime: number;
}): ElementsTrack[] => {
  const fps = getClipFPS(clip);
  const selectionStartFrame = Math.round(playerStartTime * fps);
  const selectionEndFrame = Math.round(playerEndTime * fps);
  const hookDurationFrames = Math.max(0, selectionEndFrame - selectionStartFrame);

  const originalElementTracks = (clip.asset_metadata.player_config?.playerConfig?.tracks ?? []).filter(
    t => t.type === TrackType.ELEMENTS
  );

  const shiftedElementTracks = shiftElementTracksRight(originalElementTracks, hookDurationFrames);
  return cloneOverlaysForHook(clip, selectionStartFrame, hookDurationFrames, shiftedElementTracks);
};

const normalizeTransientSplitClip = (clip: Clip): Clip => {
  updateCaptions(clip);
  clip.asset_metadata.player_config = getPlayerConfigHelper({ clip, shouldUpdatePlayerConfig: true });

  return clip;
};

const getClipAfterTransientSplit = (baseClip: Clip, splitPlayerTime: number): Clip => {
  const splitClip = injectSegmentMetaDataInCompositeSegment(baseClip);
  splitSegment(splitClip, splitPlayerTime);
  return normalizeTransientSplitClip(splitClip);
};

export const isolateHookSelectionSegment = ({
  baseClip,
  startTime,
  endTime,
  segmentId
}: {
  baseClip: Clip;
  startTime: number;
  endTime: number;
  segmentId: string;
}) => {
  const [selectionStartTime, selectionEndTime] = startTime <= endTime ? [startTime, endTime] : [endTime, startTime];
  let playerStartTime = getPlayerTimeFromSegmentTime(selectionStartTime, baseClip, segmentId);
  const playerStartTimeAtZero = approxLTE(playerStartTime, 0);
  let clipAfterStartSplit: Clip;
  let segmentCreatedByStartSplit: ReturnType<typeof getAllSegments>[number];
  // Skip splitting at the start edge — splitting at time 0 would create a zero-length
  // segment, causing issues when creating a hook at the beginning of a segment.
  if (playerStartTimeAtZero) {
    playerStartTime = 0;
    clipAfterStartSplit = baseClip;
    segmentCreatedByStartSplit = getAllSegments(clipAfterStartSplit).find(s => s.id === segmentId)!;
  } else {
    clipAfterStartSplit = getClipAfterTransientSplit(baseClip, playerStartTime);

    const segmentsAfterStartSplit = getAllSegments(clipAfterStartSplit);
    const originalSegmentIdx = segmentsAfterStartSplit.findIndex(s => s.id === segmentId);
    segmentCreatedByStartSplit = segmentsAfterStartSplit[originalSegmentIdx + 1];
    if (!segmentCreatedByStartSplit) {
      throw new Error('CREATE_HOOK failed: start split did not isolate selection segment');
    }
  }

  let playerEndTime = getPlayerTimeFromSegmentTime(
    selectionEndTime,
    clipAfterStartSplit,
    segmentCreatedByStartSplit.id
  );
  const clipDuration = (baseClip.asset_metadata.player_config?.durationInFrames || 0) / getClipFPS(baseClip);
  const playerEndTimeAtEnd = approxLTE(clipDuration, playerEndTime);

  let isolatedSelectionSegment: ReturnType<typeof getAllSegments>[number] | undefined;
  // Skip splitting at the end edge — splitting at the clip duration boundary would create
  // a zero-length segment, causing issues when creating a hook at the end of a segment.
  if (playerEndTimeAtEnd) {
    playerEndTime = clipDuration;
    isolatedSelectionSegment = getAllSegments(clipAfterStartSplit).find(s => s.id === segmentCreatedByStartSplit.id)!;
  } else {
    const clipAfterEndSplit = getClipAfterTransientSplit(clipAfterStartSplit, playerEndTime);
    isolatedSelectionSegment = getAllSegments(clipAfterEndSplit).find(s => s.id === segmentCreatedByStartSplit.id);

    if (!isolatedSelectionSegment) {
      throw new Error('CREATE_HOOK failed: end split did not preserve isolated selection segment');
    }
  }

  return {
    isolatedSelectionSegment,
    playerStartTime,
    playerEndTime
  };
};
